import 'twin.macro'

import CashStackIcon from 'assets/icons/cash-stack-icon.svg?component'
import PercentageIcon from 'assets/icons/percentage-icon.svg?component'
import StarIcon from 'assets/icons/star-icon.svg?component'
import Alert from 'components/UI/Alert'
import ConditionBlock, { ConditionProps } from 'components/UI/Blocks/ConditionBlock'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import UpgradeConditions from 'components/views/UpgradeOwnLoanConditions'
import { useAppSelector } from 'hooks'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getCreditParamsSelector, getCreditProgramSelector, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getUserRecord } from 'redux/store/slices/userSlice'
import tw from 'twin.macro'
import { numberWithSpaces, removeExtraWhitespace } from 'utils'
import { conditionBlockStyles } from 'utils/constants'

const UpgradeConditionsPage = () => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const creditProgram = useAppSelector(getCreditProgramSelector)
  const { first_name, middle_name, last_name } = useAppSelector(getUserRecord)
  const { interest_rate, loan_amount } = useAppSelector(getCreditParamsSelector)
  const { credit_program_verbose, credit_program_verbose_kk } = useAppSelector(getStatusSelector)

  // Computed values
  const loanConditions = useMemo<Array<ConditionProps>>(
    () => [
      {
        caption: t('loan-conditions.loan-amount', { ns: 'common' }),
        body: `${numberWithSpaces(Number(loan_amount).toFixed(0))} ₸`,
        icon: <CashStackIcon />
      },
      {
        caption: t('loan.interest-rate', { ns: 'common' }),
        body: `${interest_rate}%`,
        icon: <PercentageIcon />
      },
      {
        caption: t('programs.title', { ns: 'common' }),
        body: `${language === 'ru' ? credit_program_verbose : credit_program_verbose_kk}`,
        icon: <StarIcon />
      }
    ],
    [interest_rate, loan_amount, credit_program_verbose, language, credit_program_verbose_kk, t]
  )

  return (
    <ContainerWithSteps>
      <div>
        <div tw='px-4 mt-3 md:px-0 md:mt-0'>
          <Heading variant='title2'>
            {removeExtraWhitespace(`${last_name ?? ''} ${first_name ?? ''} ${middle_name ?? ''}`)},{' '}
            <span tw='text-red-light'>{t('flow-extra-docs')}</span>
            {t('flow-extra-docs2')}
          </Heading>
          {loan_amount && +loan_amount !== 0 ? (
            <Typography variant='subbody' twStyle={tw`mt-2 text-grey-dark`}>
              {t('flow-upgrade-dis')}
              {creditProgram === 'DAMU' ? 'Даму' : creditProgram === 'OWN_PROGRAM' ? t('flow-upgrade-dis-mid') : ''}
              {t('flow-upgrade-dis2')}
            </Typography>
          ) : (
            <Typography variant='subbody' twStyle={tw`mt-2 text-grey-dark`}>
              {t('flow-upload-atleast')}
            </Typography>
          )}
        </div>
        {loan_amount && +loan_amount !== 0 ? (
          <Fragment>
            <InfoBlock twStyle={tw`pl-0 my-4`}>
              <Heading variant='subtitle' twStyle={tw`pl-5`}>
                {t('flow-bank-offer')}
              </Heading>
              <div tw='grid grid-cols-2 mt-[19px] border-t-[1px] border-stroke'>
                {loanConditions.map((item, id) => {
                  return (
                    <ConditionBlock
                      twStyle={conditionBlockStyles}
                      key={`condition_${id}`}
                      body={item.body}
                      caption={item.caption}
                      icon={item.icon}
                    />
                  )
                })}
              </div>
            </InfoBlock>
            <InfoBlock twStyle={tw`my-4`}>
              <Heading variant='subtitle' twStyle={tw`mb-5`}>
                {t('flow-fin-state')}
              </Heading>
              <Alert>
                <Typography text twStyle={tw`text-[14px] leading-[26px] tracking-[-0.15px]`}>
                  {t('flow-upload-dis')}
                  <br />
                  {t('flow-pass')}
                  <span tw='font-semibold'>{t('flow-pass-mid')}</span>
                  {t('flow-pass-end')}
                </Typography>
              </Alert>
            </InfoBlock>
          </Fragment>
        ) : null}
        <UpgradeConditions />
      </div>
    </ContainerWithSteps>
  )
}

export default UpgradeConditionsPage
